/* Custom label personalization block — product page (Dawn 15.x)
   - Forces UPPERCASE and strips characters the label font can't print
   - Line 2 only enabled once line 1 has text
   - Live preview with auto-fit text, switches 8oz/4oz artwork on variant change
   - Empty fields are skipped at submit so blank properties never reach the cart */

if (!customElements.get('custom-label')) {
  customElements.define(
    'custom-label',
    class CustomLabel extends HTMLElement {
      static ALLOWED = /[^A-Z0-9 &'\-.,!?]/g;

      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        this.line1 = this.querySelector('[data-line="1"]');
        this.line2 = this.querySelector('[data-line="2"]');
        this.notice = this.querySelector('[data-notice]');
        this.errorEl = this.querySelector('[data-error]');
        this.required = this.dataset.requireLine1 === 'true';
        this.preview = this.querySelector('[data-preview]');
        this.stage = this.querySelector('[data-stage]');
        this.zone = this.querySelector('[data-zone]');
        this.pv1 = this.querySelector('[data-preview-line="1"]');
        this.pv2 = this.querySelector('[data-preview-line="2"]');
        this.placeholder = this.pv1.textContent;
        this.previews = [this.preview];
        this.views = [{ zone: this.zone, l1: this.pv1, l2: this.pv2 }];
        try {
          this.sizes = JSON.parse(this.dataset.sizes || '{}');
        } catch (e) {
          this.sizes = {};
        }
        this.inputs = [this.line1, this.line2].filter(Boolean);
        this.inputs.forEach((input) => input.addEventListener('input', () => this.onInput(input)));

        // Capture phase on document runs before Dawn's product-form submit handler builds FormData.
        this.onSubmit = this.onSubmit.bind(this);
        document.addEventListener('submit', this.onSubmit, true);

        if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange) {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data && data.variant && data.variant.id) this.setSize(data.variant.id);
          });
        }

        this.resizeObserver = new ResizeObserver(() => this.render());
        this.resizeObserver.observe(this.stage);
        this.querySelectorAll('img').forEach((img) => img.addEventListener('load', () => this.render()));

        // Promote the live preview to the top of the media column so it acts as the
        // main product photo. Refs above are captured first, so they survive the move.
        // Skipped inside quick-add modals and when the product has no media gallery.
        if (!this.closest('quick-add-modal')) {
          const gallery = document.getElementById(this.id.replace('CustomLabel-', 'MediaGallery-'));
          if (gallery && gallery.parentElement) {
            gallery.parentElement.insertBefore(this.preview, gallery);
            this.preview.classList.add('custom-label__preview--main');
            // Companion mini preview beside the fields, shown only below the two-column
            // breakpoint: on mobile the main preview scrolls out of view while typing.
            const mini = document.createElement('div');
            mini.className = 'custom-label__preview custom-label__preview--mini';
            mini.dataset.size = this.preview.dataset.size || '8oz';
            const stage = this.stage.cloneNode(true);
            mini.appendChild(stage);
            this.appendChild(mini);
            this.previews.push(mini);
            this.views.push({
              zone: stage.querySelector('[data-zone]'),
              l1: stage.querySelector('[data-preview-line="1"]'),
              l2: stage.querySelector('[data-preview-line="2"]'),
            });
            this.resizeObserver.observe(stage);
            stage.querySelectorAll('img').forEach((img) => img.addEventListener('load', () => this.render()));
          }
        }

        const idInput = this.form && this.form.querySelector('[name="id"]');
        if (idInput && idInput.value) this.setSize(idInput.value);

        // Browser may restore previous text (bfcache/autofill): normalise it.
        this.inputs.forEach((input) => this.clean(input));
        this.sync();
      }

      disconnectedCallback() {
        document.removeEventListener('submit', this.onSubmit, true);
        if (this.unsubscribe) this.unsubscribe();
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      setSize(variantId) {
        const size = this.sizes[String(variantId)] || '8oz';
        this.previews.forEach((p) => (p.dataset.size = size));
        this.render();
      }

      /* Uppercase + strip disallowed characters, keeping the caret in place. Returns true if anything was stripped. */
      clean(input) {
        const raw = input.value;
        const start = input.selectionStart;
        const upper = raw.toUpperCase();
        const cleaned = upper.replace(CustomLabel.ALLOWED, '');
        if (cleaned !== raw) {
          input.value = cleaned;
          const pos = Math.max(0, (start == null ? cleaned.length : start) - (raw.length - cleaned.length));
          try {
            input.setSelectionRange(pos, pos);
          } catch (e) {
            /* ignore */
          }
        }
        return upper !== cleaned;
      }

      onInput(input) {
        if (this.clean(input)) this.flashNotice();
        if (input === this.line1 && this.errorEl) {
          this.errorEl.hidden = true;
          this.line1.removeAttribute('aria-invalid');
        }
        this.sync();
      }

      sync() {
        this.inputs.forEach((input) => {
          const counter = this.querySelector(`[data-count="${input.dataset.line}"]`);
          if (counter) counter.textContent = input.value.length;
        });
        if (this.line2) {
          const hasLine1 = this.line1.value.trim().length > 0;
          this.line2.disabled = !hasLine1;
          if (!hasLine1 && this.line2.value) {
            this.line2.value = '';
            const counter = this.querySelector('[data-count="2"]');
            if (counter) counter.textContent = '0';
          }
        }
        this.render();
      }

      flashNotice() {
        if (!this.notice) return;
        this.notice.hidden = false;
        clearTimeout(this.noticeTimer);
        this.noticeTimer = setTimeout(() => (this.notice.hidden = true), 3500);
      }

      render() {
        const t1 = this.line1.value.trim();
        const t2 = this.line2 ? this.line2.value.trim() : '';
        this.views.forEach((v) => {
          v.l1.textContent = t1 || this.placeholder;
          v.l1.classList.toggle('is-placeholder', !t1);
          v.l2.textContent = t1 ? t2 : '';

          const h = v.zone.clientHeight;
          const w = v.zone.clientWidth;
          if (!h || !w) return;
          // proportions from the real 8oz label: name is ~30% of the name-block height, notes ~14%
          this.fit(v.l1, h * 0.3, w, 8);
          if (t2) this.fit(v.l2, h * 0.14, w, 6);
        });
      }

      /* Start at maxPx and shrink until the single line fits the zone width. */
      fit(el, maxPx, maxWidth, minPx) {
        el.style.fontSize = `${maxPx}px`;
        const width = el.scrollWidth;
        if (width > maxWidth) {
          el.style.fontSize = `${Math.max(minPx, Math.floor(maxPx * (maxWidth / width) * 0.98))}px`;
        }
      }

      onSubmit(event) {
        if (!this.form || event.target !== this.form) return;
        // Dawn's form is novalidate; block submit ourselves when the name is required.
        // Skip when the block isn't visible (e.g. hidden inside the quick-add modal).
        if (this.required && this.offsetParent !== null && !this.line1.value.trim()) {
          event.preventDefault();
          event.stopPropagation();
          if (this.errorEl) this.errorEl.hidden = false;
          this.line1.setAttribute('aria-invalid', 'true');
          this.line1.focus();
          return;
        }
        const empty = this.inputs.filter((input) => !input.disabled && !input.value.trim());
        empty.forEach((input) => (input.disabled = true));
        // Dawn reads FormData synchronously in its own submit handler; re-enable right after.
        setTimeout(() => empty.forEach((input) => (input.disabled = false)), 0);
      }
    }
  );
}
